import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import { Markdown } from "tiptap-markdown"

export default class extends Controller {
  static targets = ["editor", "input", "toolbar"]
  static values = { content: String }

  connect() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { class: "text-primary font-bold border-b-2 border-primary/10 hover:border-primary transition-all" },
        }),
        Image.configure({
          HTMLAttributes: { class: "w-full rounded-xl my-10 shadow-lg" },
        }),
        Markdown,
      ],
      content: this.contentValue ? this.markdownToContent(this.contentValue) : "",
      editorProps: {
        attributes: {
          class: "prose-cascadia min-h-[500px] focus:outline-none",
        },
      },
      onUpdate: ({ editor }) => {
        this.inputTarget.value = editor.storage.markdown.getMarkdown()
      },
    })

    // Set initial markdown value
    if (this.contentValue) {
      this.inputTarget.value = this.contentValue
    }
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
    }
  }

  markdownToContent(markdown) {
    return markdown
  }

  toggleBold(event) {
    event.preventDefault()
    this.editor.chain().focus().toggleBold().run()
  }

  toggleItalic(event) {
    event.preventDefault()
    this.editor.chain().focus().toggleItalic().run()
  }

  toggleLink(event) {
    event.preventDefault()
    const previousUrl = this.editor.getAttributes("link").href
    const url = window.prompt("URL", previousUrl)
    if (url === null) return
    if (url === "") {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
    } else {
      this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
    }
  }

  toggleBulletList(event) {
    event.preventDefault()
    this.editor.chain().focus().toggleBulletList().run()
  }

  toggleBlockquote(event) {
    event.preventDefault()
    this.editor.chain().focus().toggleBlockquote().run()
  }

  toggleCodeBlock(event) {
    event.preventDefault()
    this.editor.chain().focus().toggleCodeBlock().run()
  }

  toggleHeading(event) {
    event.preventDefault()
    this.editor.chain().focus().toggleHeading({ level: 2 }).run()
  }

  insertImage(event) {
    event.preventDefault()
    const choice = window.prompt("Type 'url' to enter an image URL, or 'upload' to upload a file", "url")

    if (choice === "url") {
      const url = window.prompt("Image URL")
      if (url) {
        this.editor.chain().focus().setImage({ src: url }).run()
      }
    } else if (choice === "upload") {
      const input = document.createElement("input")
      input.type = "file"
      input.accept = "image/*"
      input.onchange = () => {
        const file = input.files[0]
        if (file) this.uploadImage(file)
      }
      input.click()
    }
  }

  uploadImage(file) {
    import("@rails/activestorage").then(({ DirectUpload }) => {
      const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads")
      upload.create((error, blob) => {
        if (error) {
          console.error("Upload failed:", error)
          window.alert("Image upload failed. Please try again.")
        } else {
          const url = `/rails/active_storage/blobs/redirect/${blob.signed_id}/${blob.filename}`
          this.editor.chain().focus().setImage({ src: url }).run()
        }
      })
    })
  }
}
